import {
  OptionInfo,
  GroupOptions,
  useDragAndDrop,
  getDefaultOption,
  groupPlaceholder,
  getTitleOptionGroup,
  StylesText,
} from '@entities/leadCollector';
import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon, SixDotsIcon, TrashIcon } from '@shared/assets/icons';
import { Checkbox, RadioButton } from '@shared/ui/kit';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import ButtonWithPlus from '@shared/ui/button-with-plus';
import { colors } from '@shared/tokens/colors.stylex';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@shared/ui/tooltip';
import { Tiptap } from '@shared/ui/tiptap';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface FormOptionGroupProps {
  onChange: (properties: GroupOptions) => void;
  properties?: unknown;
  isRadio?: boolean;
  isCanEdit: boolean;
  value: Record<string, unknown>;
}

export const FormOptionGroup = ({ onChange, properties, isRadio, isCanEdit, value }: FormOptionGroupProps) => {
  const [options, setOptions] = useState<OptionInfo[]>([(value.options as OptionInfo) || getDefaultOption()]);
  const [isTitleVisible, setIsTitleVisible] = useState<boolean>(Boolean(value.isTitleVisible) || true);
  const titleRef = useRef<HTMLSpanElement>(null);
  const isShowRadioError = isRadio && options.every(({ isActive, error }) => !isActive && error);

  const handleReorderOptions = (dragIndex: number, dropIndex: number) => {
    [options[dragIndex], options[dropIndex]] = [options[dropIndex], options[dragIndex]];
    setOptions(options);
    onChangeGroup();
  };

  const { handleDragStart, handleDragEnd, handleDragOver, handleDrop } = useDragAndDrop({
    onReorder: handleReorderOptions,
  });

  const handleBlurTitle = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (titleRef.current && !e.currentTarget.textContent?.trim()) {
      titleRef.current.textContent = groupPlaceholder;
    }

    onChangeGroup();
  };

  const handleFocusTitle = () => {
    if (titleRef.current && titleRef.current.textContent === groupPlaceholder) {
      titleRef.current.textContent = '';
      titleRef.current.focus();
    }
  };

  const onChangeText = (html: string, id: string) => {
    const changedItemIndex = options.findIndex(({ instanceId }) => instanceId === id);
    options[changedItemIndex].text = html;
    setOptions(options);
    onChangeGroup();
  };

  const handleAddItem = () => {
    setOptions((prev) => [...prev, getDefaultOption()]);
  };

  const handleDeleteItem = (id: string) => {
    setOptions(options.filter(({ instanceId }) => instanceId !== id));
  };

  const onChangeGroup = () => {
    onChange({ title: titleRef.current?.textContent || '', isTitleVisible, options: options });
  };

  const onChooseRadio = (instanceId: string) => {
    setOptions((prev) => prev.map((option) => ({ ...option, isActive: option.instanceId === instanceId })));
  };

  const onClickCheckbox = (instanceId: string) => {
    setOptions((prev) =>
      prev.map((option) => ({
        ...option,
        isActive: option.instanceId === instanceId ? !option.isActive : option.isActive,
      }))
    );
  };

  useEffect(() => {
    onChangeGroup();
  }, [options, isTitleVisible]);

  useEffect(() => {
    if (!value || !titleRef.current || !value.options) return;

    setIsTitleVisible(!!value.isTitleVisible);
    setOptions(value.options as OptionInfo[]);
    titleRef.current.textContent = value.title?.toString() || '';
  }, []);

  return (
    <div {...stylex.props(styles.container)} draggable={'false'}>
      <div {...stylex.props(styles.titleContainer, isCanEdit && !isTitleVisible && styles.titleHidden)}>
        <span
          {...stylex.props(styles.title, !isTitleVisible && styles.titleDisable)}
          suppressContentEditableWarning
          contentEditable={isTitleVisible && !isCanEdit}
          onBlur={handleBlurTitle}
          onFocusCapture={handleFocusTitle}
          ref={titleRef}
          onMouseDownCapture={() => titleRef.current?.focus()}
        >
          {titleRef.current?.textContent || groupPlaceholder}
        </span>

        {!isCanEdit && (
          <UnstyledButton onClick={() => setIsTitleVisible(!isTitleVisible)}>
            <span {...stylex.props(styles.changeVisibleButton, !isTitleVisible && styles.changeVisibleButtonDisable)}>
              <Tooltip position="topRight" title={getTitleOptionGroup(isTitleVisible)} isLightTheme>
                <span>{isTitleVisible ? <EyeSlashIcon color="currentColor" /> : <EyeIcon color="currentColor" />}</span>
              </Tooltip>
            </span>
          </UnstyledButton>
        )}
      </div>

      <div {...stylex.props(styles.items)}>
        {options.map(({ instanceId, text, isActive, error }, index) => {
          const isShowCheckboxError = !isActive && !isRadio && error;

          return (
            <div
              key={instanceId}
              {...stylex.props(isRadio ? styles.radioButton : styles.checkbox, !!error && styles.optionError)}
              draggable={!isCanEdit}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragStart={(e) => handleDragStart(e, index)}
            >
              {isRadio ? (
                <RadioButton changeFunction={() => onChooseRadio(instanceId)} isChecked={isActive} size="medium" />
              ) : (
                <Checkbox onChange={() => onClickCheckbox(instanceId)} isChecked={isActive} />
              )}
              <div {...stylex.props(styles.tiptapContainer)}>
                <Tiptap
                  {...stylex.props(styles.text)}
                  onChange={(html) => onChangeText(html, instanceId)}
                  properties={properties}
                  isInline
                  hiddenStyles={[StylesText.LEFT, StylesText.CENTER, StylesText.RIGHT, StylesText.JUSTIFY]}
                  content={text}
                  editable={!isCanEdit}
                />

                {isShowCheckboxError && <span {...stylex.props(styles.error)}>{error}</span>}
              </div>

              {!isCanEdit && (
                <div {...stylex.props(styles.actions)}>
                  <UnstyledButton onClick={() => {}}>
                    <span {...stylex.props(styles.moveButton)}>
                      <SixDotsIcon color={colors.secondaryBackground} />
                    </span>
                  </UnstyledButton>

                  <UnstyledButton onClick={() => handleDeleteItem(instanceId)}>
                    <span {...stylex.props(styles.removeButton)}>
                      <TrashIcon />
                    </span>
                  </UnstyledButton>
                </div>
              )}
            </div>
          );
        })}

        {isShowRadioError && (
          <div {...stylex.props(styles.errorContainer)}>
            <ExclamationCircleIcon />
            <span {...stylex.props(styles.errorText)}>Должно быть выбрано одно значение</span>
          </div>
        )}

        {!isCanEdit && <ButtonWithPlus handleClick={handleAddItem} labelText="Добавить" />}
      </div>
    </div>
  );
};
